import { ReactComponent as ArrowIcon } from 'img/icons/arrowBack.svg';
import { ReactComponent as CoEditIcon } from 'img/icons/coedit.svg';
import { ReactComponent as CloseIcon } from 'mrg-icons/src/mailru/actions/close_big.svg';
import { ReactComponent as CloudIcon } from 'mrg-icons/src/mailru/logotypes/cloud.svg';
import React, { createRef, PureComponent, ReactElement } from 'react';
import { connect } from 'react-redux';
import { downloadItem, getCurrentState, goTo, updateItemFavoriteFlag } from 'reactApp/appHelpers/appHelpers';
import { IS_MY_TEAM } from 'reactApp/appHelpers/configHelpers';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { renderSharingNew } from 'reactApp/components/SharingNew/SharingNew.helpers';
import { EFrom } from 'reactApp/components/SharingNew/SharingNew.types';
import { getEditorItem } from 'reactApp/modules/editor/editor.selectors';
import { EditorItem } from 'reactApp/modules/editor/editor.types';
import { addToFavorites, removeFromFavorites } from 'reactApp/modules/modifying/modifying.actions';
import { renameRequest } from 'reactApp/modules/modifying/sagas/rename.saga';
import { goToRegisterPage } from 'reactApp/modules/ph/ph.helpers';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUser } from 'reactApp/modules/user/user.thunkActions';
import styles from 'reactApp/ui/EditorHeader/EditorHeader.css';
import { HeaderButton } from 'reactApp/ui/EditorHeader/HeaderButton';
import { HeaderFileName } from 'reactApp/ui/EditorHeader/HeaderFileName';
import { EColor, FavoriteIcon } from 'reactApp/ui/FavoriteIcon/FavoriteIcon';
import Tooltip from 'reactApp/ui/Tooltip/Tooltip';
import { DownloadIcon, LinkIcon, ShareIcon, UploadIcon } from 'reactApp/ui/VKUIIcons';
import { createGaSender, sendGa } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';

const updateWeblinkInUrlAction = (id, weblink): void => {
    const state = getCurrentState();

    if (!state) {
        return;
    }

    const { ...params } = state.params;

    if (weblink) {
        params.weblink = weblink;
    } else {
        delete params.weblink;
    }

    goTo({ id, params });
};

interface MapState {
    backLink: string;
    updateWeblinkInUrl(id: EditorItem['id'] | undefined, weblink: EditorItem['weblink']);
    renameItem(file: EditorItem | null, newName: string);
    isAnonymous: boolean;
    file: EditorItem | null;
    id?: string;
}

interface Props extends MapState {
    showBubble: boolean;
    onBubbleShow();
    toggleFavorite(file: EditorItem);
    loadUser();
    showNotify(options);
    onBackToCloud: (() => void) | null;
    isPublic: boolean;
    isStock: boolean;
    enableFavorites: boolean;
    gaCategory?: string;
    itemSelector(state);
    returnButtonTitle?: string;
    isViewer?: boolean;
    itemStorage?: EStorageType | null;
    licenseExpired?: boolean;
    downloadDropdownList?: any[];
}

interface State {
    showBubble: boolean;
    isDownloadOpen: boolean;
}

const mapStateToProps = (state, props): MapState => {
    const { isPublic, itemSelector = getEditorItem } = props;

    const isAnonymous = UserSelectors.isAnonymous(state);
    const file = itemSelector(state);
    const myTeamParam = IS_MY_TEAM ? '?wv=1' : '';

    return {
        backLink: isPublic ? `/home${myTeamParam}` : `/home${file?.parent}${myTeamParam}`,
        updateWeblinkInUrl: updateWeblinkInUrlAction,
        renameItem: renameRequest,
        isAnonymous,
        file,
        id: file?.id,
    };
};

const mapDispatchToProps = (dispatch) => ({
    loadUser: (): void => dispatch(loadUser()),
    showNotify: (options): void => dispatch(showSnackbarAction(options)),
    toggleFavorite: (item: EditorItem): void =>
        dispatch(item.isInFavorites ? removeFromFavorites({ items: [item] }) : addToFavorites({ items: [item], from: 'editor' })),
});

export class EditorHeaderComponent extends PureComponent<Props, State> {
    static defaultProps = {
        showBubble: false,
        onBubbleShow: noop,
        toggleFavorite: noop,
        updateWeblinkInUrl: noop,
        renameItem: noop,
        loadUser: noop,
        showNotify: noop,
        isPublic: false,
        isStock: false,
        enableFavorites: false,
        isAnonymous: false,
        backLink: '',
        file: null,
        id: '',
        onBackToCloud: null,
        gaCategory: '',
        returnButtonTitle: 'Вернуться в Облако',
    };

    public readonly state: State = {
        showBubble: false,
        isDownloadOpen: false,
    };

    buttonTextRef = createRef<HTMLDivElement>();

    gaSender = createGaSender(`${this.props.gaCategory}-${this.props.isPublic ? 'public' : 'home'}`);

    componentDidMount(): void {
        const { file, onBubbleShow, showBubble, loadUser, isPublic, updateWeblinkInUrl } = this.props;
        this.gaSender('show');

        if (isPublic) {
            sendGa('public-edit', 'file-type', 'document');
            const ext = file?.ext;
            if (ext) {
                sendGa('public-edit', 'file-extention', ext);
            }
        } else {
            updateWeblinkInUrl(file?.id, file?.weblink);
        }

        if (this.buttonTextRef.current && showBubble) {
            onBubbleShow();
            this.setState({
                showBubble: true,
            });
        }

        loadUser();
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.file?.id !== this.props.file?.id || prevProps.file?.weblink !== this.props.file?.weblink) {
            this.props.updateWeblinkInUrl(this.props.file?.id, this.props.file?.weblink);
        }
    }

    handlePublish = (e?: React.MouseEvent<HTMLDivElement>): void => {
        const { file } = this.props;

        e?.stopPropagation();

        this.gaSender('publish', 'click');

        this.setState({
            showBubble: false,
        });

        publishHelper({ item: file, gaSuffix: '_header' });
    };

    handleShareDocument = () => {
        const { id = '' } = this.props;

        this.gaSender('share', 'click');

        renderSharingNew({ id, from: EFrom.SHARING });
    };

    handleToggleFavorite = (): void => {
        const { toggleFavorite, file } = this.props;
        this.gaSender('favorite', 'click');
        const isInFavoritesPrev = !!file?.isInFavorites;

        if (!file) {
            return;
        }

        toggleFavorite(file);
        this.gaSender('favorite', 'success');
        updateItemFavoriteFlag(file?.id, !isInFavoritesPrev);
    };

    handleFileNameClick = (): void => this.gaSender('rename', 'click');

    handleFileNameEdit = ({ value }): Promise<any> => {
        const { showNotify, file, renameItem } = this.props;

        const trimedValue = value.trim();

        if (trimedValue === file?.nameWithoutExt) {
            return Promise.resolve();
        }

        if (!trimedValue) {
            showNotify({
                type: SnackbarTypes.failure,
                text: 'Название не может быть пустым',
                id: 'rename',
                closable: true,
            });
            return Promise.reject();
        }

        const newName = `${trimedValue}.${file?.ext}`;

        return renameItem(file, newName)
            .then(() => {
                this.gaSender('rename', 'success');

                goTo({ id: this.props.file?.id });
            })
            .catch((error) => {
                this.gaSender('rename', 'error');
                showNotify({
                    type: SnackbarTypes.failure,
                    text: error,
                    id: 'rename',
                    closable: true,
                });
                return Promise.reject();
            });
    };

    handleDownload = (): void => {
        const { file } = this.props;

        this.gaSender('download', 'click');

        downloadItem({ itemOrId: file });
    };

    handleDownloadDropdown = () => {
        this.setState(({ isDownloadOpen }) => ({
            isDownloadOpen: !isDownloadOpen,
        }));
    };

    handleSaveToCloud = (): void => {
        const fileId = this.props.file?.id;

        this.gaSender('savetocloud', 'click');

        if (!fileId) {
            return;
        }

        toolbarActions.clone({ id: fileId, source: 'editor' });
    };

    closeBubble = (): void => {
        this.setState({
            showBubble: false,
        });
    };

    private renderRightGroup = (): ReactElement | null => {
        const { file, isPublic, isStock, downloadDropdownList } = this.props;
        const { showBubble, isDownloadOpen } = this.state;

        if (isStock) {
            return null;
        }

        const accessTitle = file?.weblink ? 'Настройки доступа' : 'Открыть доступ';

        return (
            <>
                <HeaderButton
                    title="Скачать"
                    icon={<DownloadIcon />}
                    onClick={this.handleDownload}
                    dropdownList={downloadDropdownList}
                    onDropdownClick={this.handleDownloadDropdown}
                    isDropdownOpen={isDownloadOpen}
                />
                {isPublic ? (
                    <HeaderButton
                        title="Поделиться ссылкой"
                        icon={<LinkIcon />}
                        onClick={this.handleShareDocument}
                        textRef={this.buttonTextRef}
                    />
                ) : (
                    <>
                        {showBubble && (
                            <Tooltip
                                theme="octavius"
                                target={this.buttonTextRef.current}
                                placement="bottomRight"
                                offset={{
                                    y: 7,
                                }}
                                smartPlacement={false}
                                pinned
                                noArrow
                            >
                                <div className={styles.bubble}>
                                    <div className={styles.bubbleClose} onClick={this.closeBubble}>
                                        <CloseIcon />
                                    </div>
                                    <div className={styles.bubbleIcon}>
                                        <CoEditIcon width={145} height={100} />
                                    </div>
                                    <div>
                                        <div className={styles.bubbleTitle}>Совместное редактирование документов</div>
                                        <div className={styles.bubbleText}>
                                            Работайте совместно. Вы можете открывать доступ к вашим документам по секретной ссылке.
                                        </div>
                                    </div>
                                </div>
                            </Tooltip>
                        )}
                        <div>
                            <HeaderButton
                                title={accessTitle}
                                icon={<ShareIcon />}
                                onClick={this.handlePublish}
                                size={181}
                                textRef={this.buttonTextRef}
                            />
                        </div>
                    </>
                )}
            </>
        );
    };

    render(): ReactElement {
        const {
            enableFavorites,
            backLink,
            file,
            isAnonymous,
            isPublic,
            onBackToCloud,
            returnButtonTitle = 'Вернуться в Облако',
            isViewer,
            licenseExpired = false,
        } = this.props;

        const showCloneToCloud = !isViewer && !isAnonymous && isPublic && !file?.home;

        return (
            <>
                <div className={styles.root}>
                    <div className={styles.leftGroup}>
                        {isAnonymous ? (
                            <HeaderButton title="Завести Облако" onClick={goToRegisterPage} icon={<CloudIcon width={20} height={20} />} />
                        ) : (
                            <HeaderButton
                                title={returnButtonTitle}
                                link={onBackToCloud ? null : backLink}
                                onClick={onBackToCloud || noop}
                                icon={<ArrowIcon width={9} height={14} viewBox="0 0 5 8" />}
                            />
                        )}
                        {showCloneToCloud && (
                            <HeaderButton title="Сохранить в Облако" icon={<UploadIcon />} onClick={this.handleSaveToCloud} />
                        )}
                    </div>
                    <div className={styles.middleGroup}>
                        <HeaderFileName
                            fileName={file?.nameWithoutExt || ''}
                            readOnly={!!isViewer || isPublic || !!file?.readOnly}
                            /**
                             * TODO: раскомментировать когда:
                             * 1) "Мой офис" научится не перенаправлять фокус в документ каждую секунду
                             * 2) "R7" будет возможность удаленного переименования */
                            // onEdit={isViewer ? noop : this.handleFileNameEdit}
                            // onClick={isViewer ? noop : this.handleFileNameClick}
                            title={file?.isInFavorites ? 'Убрать из избранных' : 'Добавить в избранные'}
                            icon={
                                enableFavorites ? (
                                    <FavoriteIcon
                                        onClick={this.handleToggleFavorite}
                                        className={styles.favoritesIcon}
                                        active={file?.isInFavorites}
                                        color={EColor.WHITE}
                                    />
                                ) : null
                            }
                        />
                    </div>
                    <div className={styles.rightGroup}>{this.renderRightGroup()}</div>
                </div>
                {licenseExpired && (
                    <div className={styles.license}>
                        <div>Количество лицензий закончилось</div>
                    </div>
                )}
            </>
        );
    }
}

export const EditorHeader = connect(mapStateToProps, mapDispatchToProps)(EditorHeaderComponent);
